import { openDB, type DBSchema } from 'idb';
import type { CanvasDocument, GeneratorConfig, StoredCanvasProject, StoredProject } from './types';

interface ScreenGridDatabase extends DBSchema {
  projects: {
    key: string;
    value: StoredProject;
    indexes: { 'by-updated': string };
  };
  canvasProjects: {
    key: string;
    value: StoredCanvasProject;
    indexes: { 'by-updated': string };
  };
  appState: {
    key: string;
    value: GeneratorConfig | CanvasDocument;
  };
}

const databasePromise = openDB<ScreenGridDatabase>('screengrid-generator', 2, {
  upgrade(database) {
    if (!database.objectStoreNames.contains('projects')) {
      const projects = database.createObjectStore('projects', { keyPath: 'id' });
      projects.createIndex('by-updated', 'updatedAt');
    }
    if (!database.objectStoreNames.contains('canvasProjects')) {
      const canvases = database.createObjectStore('canvasProjects', { keyPath: 'id' });
      canvases.createIndex('by-updated', 'updatedAt');
    }
    if (!database.objectStoreNames.contains('appState')) database.createObjectStore('appState');
  },
});

export async function saveDraft(config: GeneratorConfig): Promise<void> {
  const database = await databasePromise;
  await database.put('appState', config, 'draft');
}

export async function loadDraft(): Promise<GeneratorConfig | undefined> {
  const database = await databasePromise;
  return (await database.get('appState', 'draft')) as GeneratorConfig | undefined;
}

export async function listProjects(): Promise<StoredProject[]> {
  const database = await databasePromise;
  const projects = await database.getAllFromIndex('projects', 'by-updated');
  return projects.reverse();
}

export async function saveProject(project: StoredProject): Promise<void> {
  const database = await databasePromise;
  await database.put('projects', project);
}

export async function deleteProject(id: string): Promise<void> {
  const database = await databasePromise;
  await database.delete('projects', id);
}

export async function saveCanvasDraft(canvas: CanvasDocument): Promise<void> {
  const database = await databasePromise;
  await database.put('appState', canvas, 'canvas-draft');
}

export async function loadCanvasDraft(): Promise<CanvasDocument | undefined> {
  const database = await databasePromise;
  return (await database.get('appState', 'canvas-draft')) as CanvasDocument | undefined;
}

export async function listCanvasProjects(): Promise<StoredCanvasProject[]> {
  const database = await databasePromise;
  const projects = await database.getAllFromIndex('canvasProjects', 'by-updated');
  return projects.reverse();
}

export async function saveCanvasProject(project: StoredCanvasProject): Promise<void> {
  const database = await databasePromise;
  await database.put('canvasProjects', project);
}

export async function deleteCanvasProject(id: string): Promise<void> {
  const database = await databasePromise;
  await database.delete('canvasProjects', id);
}
